import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  List, 
  Avatar,
  Tag,
  Button,
  Spin,
  Empty,
  Space,
  Badge,
  Alert
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import './Dashboard.css';

const { Text, Title } = Typography;

function AdminDashboard() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [branchData, setBranchData] = useState({});
  const [studentCounts, setStudentCounts] = useState([]);
  const [classCounts, setClassCounts] = useState([]);
  const [teacherCounts, setTeacherCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError('Failed to fetch branches');
    }
  }, [API_URL]);

  // Fetch student counts by branch using new API
  const fetchStudentCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/students/count-students-branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentCounts(response.data);
    } catch (error) {
      console.error('Error fetching student counts:', error);
      setStudentCounts([]);
    }
  }, [API_URL]);

  // Fetch class counts by branch using new API
  const fetchClassCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups/count-class-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassCounts(response.data);
    } catch (error) {
      console.error('Error fetching class counts:', error);
      setClassCounts([]);
    }
  }, [API_URL]);

  // Fetch teacher counts (keep existing logic or create similar endpoint)
  const fetchTeacherCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch for each branch
      const teacherPromises = branches.map(async (branch) => {
        try {
          const response = await axios.get(`${API_URL}/teachers/count`, {
            headers,
            params: { branchId: branch.id }
          });
          return {
            branch_id: branch.id,
            total_teachers: response.data.total_teachers || 0
          };
        } catch (error) {
          console.error(`Error fetching teachers for branch ${branch.id}:`, error);
          return {
            branch_id: branch.id,
            total_teachers: 0
          };
        }
      });

      const results = await Promise.all(teacherPromises);
      setTeacherCounts(results);
    } catch (error) {
      console.error('Error fetching teacher counts:', error);
      setTeacherCounts([]);
    }
  }, [API_URL, branches]);

  // Fetch today's classes for all branches
  const fetchTodayClassesForAllBranches = useCallback(async () => {
    setClassesLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const classPromises = branches.map(async (branch) => {
        try {
          const response = await axios.get(`${API_URL}/classes/branch-today-dashboard`, {
            headers,
            params: { branchId: branch.id }
          });
          
          const sortedClasses = response.data.sort((a, b) => {
            return a.schedule_time.localeCompare(b.schedule_time);
          });
          
          return {
            branchId: branch.id,
            classes: sortedClasses
          };
        } catch (error) {
          console.error(`Error fetching classes for branch ${branch.id}:`, error);
          return {
            branchId: branch.id,
            classes: []
          };
        }
      });

      const results = await Promise.all(classPromises);
      const branchClassData = {};
      results.forEach(result => {
        branchClassData[result.branchId] = result.classes;
      });
      
      setBranchData(branchClassData);
    } catch (error) {
      console.error('Error fetching branch classes:', error);
      setError('Failed to fetch branch classes');
    } finally {
      setClassesLoading(false);
    }
  }, [API_URL, branches]);

  // Initial load
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchBranches(),
          fetchStudentCounts(),
          fetchClassCounts()
        ]);
      } catch (error) {
        console.error('Error initializing:', error);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [fetchBranches, fetchStudentCounts, fetchClassCounts]);

  // Fetch teacher counts and classes after branches are loaded
  useEffect(() => {
    if (branches.length > 0) {
      fetchTeacherCounts();
      fetchTodayClassesForAllBranches();
    }
  }, [branches, fetchTeacherCounts, fetchTodayClassesForAllBranches]);

  // Helper functions
  const getCountForBranch = (counts, branchId) => {
    const found = counts.find(item => item.branch_id === branchId);
    return found ? (found.total_students || found.total_classes || found.total_teachers || 0) : 0;
  };

  const getStatusConfig = (status) => {
    const configs = {
      'approved': { color: 'green', icon: <CheckCircleOutlined />, text: 'Class Taught Approved' },
      'pending': { color: 'warning', icon: <PauseCircleOutlined />, text: 'Class Taught Pending' },
      'default': { color: 'default', icon: <ClockCircleOutlined />, text: 'Unknown' }
    };
    return configs[status] || configs.default;
  };

  const formatTime = (timeString) => {
    return moment(timeString, 'HH:mm').format('HH:mm');
  };

  const calculateEndTime = (startTime, duration) => {
    if (!duration) return 'N/A';
    
    let durationMinutes = 0;
    if (typeof duration === 'string') {
      if (duration.includes('h') && duration.includes('m')) {
        const hourMatch = duration.match(/(\d+)h/);
        const minuteMatch = duration.match(/(\d+)m/);
        durationMinutes = (hourMatch ? parseInt(hourMatch[1]) * 60 : 0) + 
                         (minuteMatch ? parseInt(minuteMatch[1]) : 0);
      } else {
        durationMinutes = parseInt(duration) || 60;
      }
    }
    
    return moment(startTime, 'HH:mm').add(durationMinutes, 'minutes').format('HH:mm');
  };

  const getClassStats = (classes) => {
    const total = classes?.length || 0;
    const completed = classes?.filter(cls => cls.status === 'approved').length || 0;
    const pending = classes?.filter(cls => cls.status === 'pending').length || 0;
    return { total, completed, pending };
  };

  // Render class item
  const renderClassItem = (classItem) => {
    const statusConfig = getStatusConfig(classItem.status);
    const endTime = calculateEndTime(classItem.schedule_time, classItem.schedule_hour);
    const studentConfirmIcon = classItem.student_confirm ? '✅' : '❌';
    const studentConfirmColor = classItem.student_confirm ? '#52c41a' : '#ff4d4f';

    return (
      <List.Item key={classItem.classid} style={{ padding: '12px 0' }}>
        <List.Item.Meta
          avatar={
            <Avatar style={{ 
              backgroundColor: statusConfig.color === 'green' ? '#52c41a' : '#faad14'
            }}>
              {classItem.subject_name?.[0] || 'C'}
            </Avatar>
          }
          title={
            <Space wrap style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '14px' }}>
                {classItem.subject_name || 'Unknown'}
              </Text>
              <span style={{ fontSize: '16px', color: studentConfirmColor }}>
                {studentConfirmIcon}
              </span>
              <Tag color={statusConfig.color} size="small">
                {statusConfig.text}
              </Tag>
            </Space>
          }
          description={
            <div style={{ width: '100%' }}>
              <div style={{ marginBottom: '8px' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  <BookOutlined /> {classItem.class_code}
                </Text>
              </div>
              <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  <ClockCircleOutlined /> {formatTime(classItem.schedule_time)} - {endTime}
                </Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  <UserOutlined /> {classItem.nickname || 'No Teacher'}
                </Text>
              </div>
              {classItem.student_nicknames && (
                <div style={{ marginTop: '8px' }}>
                  <Space size={4} wrap style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                      Students:
                    </Text>
                    {classItem.student_nicknames.split(', ').map((student, index) => (
                      <Tag 
                        key={index}
                        size="small"
                        style={{ 
                          margin: '0 2px',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          border: '1px solid #bbdefb',
                          fontSize: '11px',
                          padding: '2px 6px'
                        }}
                      >
                        {student.trim()}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  // Render branch card
  const renderBranchCard = (branch) => {
    const branchClasses = branchData[branch.id] || [];
    const classStats = getClassStats(branchClasses);
    const studentCount = getCountForBranch(studentCounts, branch.id);
    const teacherCount = getCountForBranch(teacherCounts, branch.id);
    const classCount = getCountForBranch(classCounts, branch.id);

    return (
      <Col xs={24} sm={24} md={12} lg={12} xl={12} key={branch.id}>
        <Card
          title={
            <Title level={4} style={{ margin: 0, textAlign: 'center', color: '#1890ff', fontSize: '18px' }}>
              {branch.name}
            </Title>
          }
          loading={classesLoading}
          style={{ height: '100%', marginBottom: '16px' }}
        >
          {/* Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={6} md={12} lg={12}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>Students</span>}
                  value={studentCount}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={12} lg={12}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>Teachers</span>}
                  value={teacherCount}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={12} lg={12}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>Courses</span>}
                  value={classCount}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: '#faad14', fontSize: '16px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={12} lg={12}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>Today's Classes</span>}
                  value={classStats.total}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#722ed1', fontSize: '16px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Today's Classes */}
          <Card 
            title={
              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong style={{ fontSize: '13px' }}>Today's Schedule</Text>
                </Space>
                <Badge count={branchClasses.length} />
              </Space>
            }
            size="small"
          >
            {branchClasses.length === 0 ? (
              <Empty 
                description="No classes today"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={branchClasses}
                renderItem={renderClassItem}
                size="small"
                style={{ maxHeight: '400px', overflowY: 'auto' }}
              />
            )}
          </Card>
        </Card>
      </Col>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px' }}>
      {/* Header */}
      <Row style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Card bodyStyle={{ padding: '16px 12px' }}>
            <Row align="middle" justify="space-between" gutter={[8, 8]}>
              <Col xs={24} sm={16} md={18}>
                <Text type="primary" style={{ fontSize: '16px', display: 'block', wordBreak: 'break-word' }}>
                  Multi-Branch Dashboard
                </Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {moment().format('dddd, DD MMMM YYYY')}
                </Text>
              </Col>
              <Col xs={24} sm={8} md={6} style={{ textAlign: 'right' }}>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={() => {
                    fetchStudentCounts();
                    fetchClassCounts();
                    fetchTeacherCounts();
                    fetchTodayClassesForAllBranches();
                  }}
                  loading={classesLoading}
                  block
                  style={{ maxWidth: '150px', marginLeft: 'auto' }}
                >
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Branch Cards */}
      <Row gutter={[16, 16]}>
        {branches.map(branch => renderBranchCard(branch))}
      </Row>

      {error && (
        <Alert 
          message={error} 
          type="error" 
          style={{ marginTop: 16 }} 
          closable 
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
}

export default withRoleAccess(AdminDashboard, ['superadmin']);